import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = async () => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        // Skip /auth/me entirely when no token — avoids a 401 on every public page.
        if (typeof window !== "undefined" && !localStorage.getItem("utal_token")) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
        } catch {
            if (localStorage.getItem("utal_token")) localStorage.removeItem("utal_token");
            setUser(null);
        }
        setLoading(false);
    };

    useEffect(() => { fetchMe(); }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.mfa_required) return { mfa: true, challenge_token: data.challenge_token };
        localStorage.setItem("utal_token", data.token);
        setUser(data.user);
        return { mfa: false, user: data.user };
    };

    const verifyMfa = async (challenge_token, code) => {
        const { data } = await api.post("/auth/mfa/verify", { challenge_token, code });
        localStorage.setItem("utal_token", data.token);
        setUser(data.user);
        return data.user;
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const loginWithGoogle = () => {
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const exchangeGoogleSession = async (session_id) => {
        const { data } = await api.post("/auth/google/session", {}, {
            headers: { "X-Session-ID": session_id },
        });
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem("utal_token");
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, verifyMfa, loginWithGoogle, exchangeGoogleSession, logout, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
